import { type InputHTMLAttributes, type ReactElement, type ReactNode, useEffect, useRef } from 'react';
import { UiIcon } from './icons';
import { cx } from './library-kit';

type InputAttrs = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'style' | 'className' | 'children' | 'onChange' | 'checked'>;

export interface CheckboxProps extends InputAttrs {
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  /** The visible label (also the accessible name). Without it, give the checkbox an `aria-label`. */
  readonly label?: ReactNode;
  /** The "some of the children" state: a dash instead of the tick (not a third value — `checked` still drives `onChange`). */
  readonly indeterminate?: boolean;
  /** A refused value: the empty box keeps a danger ring. */
  readonly invalid?: boolean;
  readonly className?: string;
}

/**
 * The checkbox (Design System: 20px, radius 6, an inset 2px ring when off, navy fill + white tick when on). The native
 * `<input type="checkbox">` covers the whole 48px-high label — it IS the hit area and the keyboard/AT target — and the drawn
 * square sits beside it, so Space, the label click and the form semantics all come from the platform.
 */
export function Checkbox({ checked, onChange, label, indeterminate = false, invalid = false, className, disabled, ...rest }: CheckboxProps): ReactElement {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current !== null) ref.current.indeterminate = indeterminate;
  }, [indeterminate, checked]);
  return (
    <label className={cx('vello-check', className)} data-disabled={disabled === true ? 'true' : undefined} data-invalid={invalid ? 'true' : undefined}>
      <input ref={ref} type="checkbox" className="vello-check__input" checked={checked} disabled={disabled} aria-invalid={invalid ? true : undefined} onChange={(e) => onChange(e.target.checked)} {...rest} />
      <span className="vello-check__box" data-kind="checkbox" aria-hidden="true">
        <span className="vello-check__mark vello-check__mark--tick">
          <UiIcon name="check" size={14} strokeWidth={3} />
        </span>
        <span className="vello-check__mark vello-check__mark--dash" />
      </span>
      {label !== undefined && <span className="vello-check__label">{label}</span>}
    </label>
  );
}

export interface RadioProps extends InputAttrs {
  readonly checked: boolean;
  /** Called with the radio's `value` when it becomes the chosen one. */
  readonly onChange: (value: string) => void;
  readonly value: string;
  /** Radios of one group share a `name` (the browser then moves the choice with the arrow keys). */
  readonly name: string;
  readonly label?: ReactNode;
  readonly className?: string;
}

/** One radio (Design System: 20px circle, an inset 6px navy ring when chosen). Use {@link RadioGroup} for a set. */
export function Radio({ checked, onChange, value, name, label, className, disabled, ...rest }: RadioProps): ReactElement {
  return (
    <label className={cx('vello-check', className)} data-disabled={disabled === true ? 'true' : undefined}>
      <input type="radio" className="vello-check__input" name={name} value={value} checked={checked} disabled={disabled} onChange={() => onChange(value)} {...rest} />
      <span className="vello-check__box" data-kind="radio" aria-hidden="true" />
      {label !== undefined && <span className="vello-check__label">{label}</span>}
    </label>
  );
}

export interface RadioOption {
  readonly value: string;
  readonly label: ReactNode;
  readonly disabled?: boolean;
}

export interface RadioGroupProps {
  /** Shared by every radio of the group; also the id base of the group. */
  readonly name: string;
  readonly options: readonly RadioOption[];
  readonly value: string | undefined;
  readonly onChange: (value: string) => void;
  /** The group's accessible name (or `labelledBy`). */
  readonly ariaLabel?: string;
  readonly labelledBy?: string;
  readonly direction?: 'column' | 'row';
  readonly disabled?: boolean;
}

/** A labelled `radiogroup` of {@link Radio}s. The arrow keys move the choice (native radio behaviour); Tab enters the chosen one. */
export function RadioGroup({ name, options, value, onChange, ariaLabel, labelledBy, direction = 'column', disabled = false }: RadioGroupProps): ReactElement {
  return (
    <div className="vello-radiogroup" role="radiogroup" aria-label={ariaLabel} aria-labelledby={labelledBy} data-direction={direction === 'row' ? 'row' : undefined}>
      {options.map((o) => (
        <Radio key={o.value} name={name} value={o.value} label={o.label} checked={o.value === value} disabled={disabled || o.disabled === true} onChange={onChange} />
      ))}
    </div>
  );
}
